import {
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCategory,
  IconChevronDown,
  IconLayoutRows,
  IconLogout,
  IconMoon,
  IconMovie,
  IconStack2,
  IconSun,
  IconTags,
  IconUsers,
  IconUsersGroup,
  IconVideo,
} from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/context';
import { can, ROLE_LABEL } from '@/auth/permissions';
import { UploadsIndicator } from '@/features/uploads/UploadsIndicator';

const NAV = [
  { to: '/programmes', label: 'Programmes', icon: IconMovie },
  { to: '/videos', label: 'Videos', icon: IconVideo },
  { to: '/creators', label: 'Creators', icon: IconUsersGroup },
  { to: '/collections', label: 'Collections', icon: IconStack2 },
  { to: '/home-layouts', label: 'Home layouts', icon: IconLayoutRows },
  { to: '/taxonomy', label: 'Taxonomy', icon: IconTags },
];

export function Wordmark() {
  return (
    <Group gap={8} wrap="nowrap">
      <Box
        aria-hidden
        w={26}
        h={26}
        style={{
          borderRadius: 8,
          background: '#ffd45e',
          border: '2px solid #434242',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <IconCategory size={14} color="#434242" />
      </Box>
      <Text fw={700} size="md">
        Playbox{' '}
        <Text span c="dimmed" fw={500}>
          Admin
        </Text>
      </Text>
    </Group>
  );
}

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { staff, logout } = useAuth();
  const location = useLocation();
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme('light');
  const items = can(staff?.role, 'manageStaff')
    ? [...NAV, { to: '/staff', label: 'Staff', icon: IconUsers }]
    : NAV;

  return (
    <AppShell
      header={{ height: 52 }}
      navbar={{ width: 216, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header px="md" className="pb-surface">
        <Group h="100%" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation"
            />
            <Wordmark />
          </Group>
          <Group gap="xs" wrap="nowrap">
            <UploadsIndicator />
            <Tooltip label={scheme === 'dark' ? 'Day theme' : 'Night theme'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Toggle colour theme"
                onClick={() => setColorScheme(scheme === 'dark' ? 'light' : 'dark')}
              >
                {scheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <UnstyledButton aria-label="Account menu">
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} visibleFrom="xs">
                      {staff?.name}
                    </Text>
                    {staff && (
                      <Badge variant="outline" color="gray">
                        {ROLE_LABEL[staff.role]}
                      </Badge>
                    )}
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{staff?.email}</Menu.Label>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void logout()}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="xs" className="pb-surface" aria-label="Main">
        <ScrollArea>
          {items.map((n) => (
            <NavLink
              key={n.to}
              component={RouterNavLink}
              to={n.to}
              label={n.label}
              leftSection={<n.icon size={18} stroke={1.8} />}
              active={location.pathname.startsWith(n.to)}
              onClick={close}
              color="sky"
              variant="light"
              style={{ borderRadius: 8 }}
            />
          ))}
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>
        <Box maw={1400} mx="auto">
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
